import { Fragment, useEffect, useMemo, useState } from "react"
import {
  Dumbbell,
  Search,
  RefreshCw,
  Download,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronRight,
  Pencil,
  Check,
  StickyNote,
} from "lucide-react"
import {
  getClassCatalog,
  updateClassCatalogEntry,
  type ClassCatalogEntry,
  type ClassCatalogFieldsInput,
  type ClassCatalogSummary,
} from "@/lib/api"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { downloadCsv } from "@/lib/csv"
import { useReadOnly } from "@/lib/read-only"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

type SortKey =
  | "studio_name"
  | "name"
  | "category"
  | "duration_min"
  | "muscle_groups"
  | "instance_count"
  | "next_date"

/** Matches backend grouping key so renames don't split a row mid-edit. */
function groupKey(c: ClassCatalogEntry): string {
  return `${c.studio_id}::${c.name.toLowerCase()}`
}

function activityColor(type: string | null): string {
  switch (type) {
    case "yoga":
      return "bg-purple-100 text-purple-800"
    case "cardio":
      return "bg-red-100 text-red-800"
    case "pilates":
      return "bg-pink-100 text-pink-800"
    case "strength":
      return "bg-blue-100 text-blue-800"
    case "recovery":
      return "bg-green-100 text-green-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

/** Muscle groups and exercises are edited as comma-separated text, split on save. */
interface ClassEditForm {
  name: string
  category: string
  duration_min: string
  muscle_groups: string
  exercise_list: string
}

function toEditForm(c: ClassCatalogEntry): ClassEditForm {
  return {
    name: c.name,
    category: c.category ?? "",
    duration_min: c.duration_min != null ? String(c.duration_min) : "",
    muscle_groups: c.muscle_groups.join(", "),
    exercise_list: c.exercise_list.join(", "),
  }
}

function splitTags(v: string): string[] {
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
}

export function ClassCatalogPage() {
  const { readOnly } = useReadOnly()
  const [classes, setClasses] = useState<ClassCatalogEntry[]>([])
  const [summary, setSummary] = useState<ClassCatalogSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [generatedAt, setGeneratedAt] = useState<string | null>(null)

  const [search, setSearch] = useState("")
  const [studioFilter, setStudioFilter] = useState<string>("all")
  const [activityFilter, setActivityFilter] = useState<string>("all")
  const [muscleGroupFilter, setMuscleGroupFilter] = useState<string>("all")
  const [missingDataOnly, setMissingDataOnly] = useState(false)

  const [sortKey, setSortKey] = useState<SortKey>("studio_name")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const [editing, setEditing] = useState<ClassCatalogEntry | null>(null)
  const [form, setForm] = useState<ClassEditForm | null>(null)
  const [saving, setSaving] = useState(false)

  function openEdit(c: ClassCatalogEntry) {
    setEditing(c)
    setForm(toEditForm(c))
  }

  function setField<K extends keyof ClassEditForm>(key: K, value: ClassEditForm[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await getClassCatalog()
      setClasses(data.classes)
      setSummary(data.summary)
      setGeneratedAt(data.generated_at)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load class catalog")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function applyUpdated(updated: ClassCatalogEntry, matchKey: string) {
    setClasses((prev) => prev.map((c) => (groupKey(c) === matchKey ? updated : c)))
  }

  async function handleSave() {
    if (!editing || !form) return
    if (!form.name.trim()) {
      toast.error("Class name can't be blank")
      return
    }
    let durationMin: number | null = null
    if (form.duration_min.trim()) {
      const parsed = Number(form.duration_min)
      if (!Number.isFinite(parsed) || parsed <= 0) {
        toast.error("Duration must be a positive number of minutes")
        return
      }
      durationMin = parsed
    }

    const updates: ClassCatalogFieldsInput = {
      name: form.name.trim(),
      category: form.category.trim() || null,
      duration_min: durationMin,
      muscle_groups: splitTags(form.muscle_groups),
      exercise_list: splitTags(form.exercise_list),
    }

    const matchKey = groupKey(editing)
    setSaving(true)
    try {
      const updated = await updateClassCatalogEntry(editing.studio_id, editing.name, updates)
      applyUpdated(updated, matchKey)
      toast.success(`Updated ${updated.name}`)
      setEditing(null)
      setForm(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update class")
    } finally {
      setSaving(false)
    }
  }

  // Inline "other details" save — optimistic, with revert-on-failure (same
  // pattern as the Users & Access notes cell).
  function handleSaveOtherDetails(entry: ClassCatalogEntry, text: string) {
    const matchKey = groupKey(entry)
    const previous = classes
    setClasses((prev) =>
      prev.map((c) => (groupKey(c) === matchKey ? { ...c, other_details: text || null } : c)),
    )
    updateClassCatalogEntry(entry.studio_id, entry.name, { other_details: text || null })
      .then((updated) => applyUpdated(updated, matchKey))
      .catch((e) => {
        setClasses(previous)
        toast.error(e instanceof Error ? e.message : "Failed to update description")
      })
  }

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
  }

  function toggleExpanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const studioOptions = useMemo(
    () => Array.from(new Set(classes.map((c) => c.studio_name))).sort((a, b) => a.localeCompare(b)),
    [classes],
  )
  const activityOptions = useMemo(
    () =>
      Array.from(new Set(classes.map((c) => c.studio_activity_type).filter(Boolean) as string[])).sort(),
    [classes],
  )
  const muscleGroupOptions = useMemo(
    () => Array.from(new Set(classes.flatMap((c) => c.muscle_groups))).sort((a, b) => a.localeCompare(b)),
    [classes],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return classes.filter((c) => {
      if (studioFilter !== "all" && c.studio_name !== studioFilter) return false
      if (activityFilter !== "all" && c.studio_activity_type !== activityFilter) return false
      if (muscleGroupFilter !== "all" && !c.muscle_groups.includes(muscleGroupFilter)) return false
      if (missingDataOnly && (c.muscle_groups.length > 0 || c.exercise_list.length > 0)) return false
      if (!q) return true
      return (
        c.studio_name.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        (c.category ?? "").toLowerCase().includes(q) ||
        c.instructors.some((i) => i.toLowerCase().includes(q)) ||
        c.muscle_groups.some((m) => m.toLowerCase().includes(q))
      )
    })
  }, [classes, search, studioFilter, activityFilter, muscleGroupFilter, missingDataOnly])

  const sorted = useMemo(() => {
    const valueOf = (c: ClassCatalogEntry): string | number => {
      switch (sortKey) {
        case "duration_min":
          return c.duration_min ?? -1
        case "muscle_groups":
          return c.muscle_groups.length
        case "instance_count":
          return c.instance_count
        case "next_date":
          return c.next_date ?? ""
        case "category":
          return (c.category ?? "").toLowerCase()
        case "name":
          return c.name.toLowerCase()
        default:
          return c.studio_name.toLowerCase()
      }
    }
    const dir = sortDir === "asc" ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      let cmp: number
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv
      else cmp = String(av).localeCompare(String(bv))
      if (cmp === 0) cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      return cmp * dir
    })
  }, [filtered, sortKey, sortDir])

  const exportCsv = () =>
    downloadCsv(
      "class-catalog.csv",
      [
        "studio", "activityType", "className", "category", "durationMin",
        "muscleGroups", "exerciseList", "otherDetails", "instructors",
        "instanceCount", "nextDate", "platform", "bookingUrl",
      ],
      sorted.map((c) => [
        c.studio_name, c.studio_activity_type ?? "", c.name, c.category ?? "",
        c.duration_min ?? "", c.muscle_groups.join("; "), c.exercise_list.join("; "),
        c.other_details ?? "", c.instructors.join("; "), c.instance_count,
        c.next_date ?? "", c.platform ?? "", c.booking_url ?? "",
      ]),
    )

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Dumbbell className="size-6" /> Class Catalog
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Readable view of the scraped <code>studio_classes</code> table — one row per
            unique class per studio, deduped across every scraped date/time instance.
            Studio and class names are resolved for you; ids stay out of the way. Edits
            here go straight to Supabase and apply to every scraped occurrence of the class.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={cn("size-4", loading && "animate-spin")} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={loading || sorted.length === 0}
          >
            <Download className="size-4" /> Export CSV
          </Button>
        </div>
      </div>

      {summary && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Data coverage — {summary.total_unique_classes} unique classes across{" "}
            {summary.studios_covered} studios ({summary.total_instances} scraped instances)
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <CoverageTile label="Category" pct={summary.pct_category} count={summary.with_category} />
            <CoverageTile label="Instructor" pct={summary.pct_instructor} count={summary.with_instructor} />
            <CoverageTile label="Duration" pct={summary.pct_duration} count={summary.with_duration} />
            <CoverageTile label="Description" pct={summary.pct_other_details} count={summary.with_other_details} />
            <CoverageTile
              label="Muscle groups"
              pct={summary.pct_muscle_groups}
              count={summary.with_muscle_groups}
              warn
            />
            <CoverageTile
              label="Exercise list"
              pct={summary.pct_exercise_list}
              count={summary.with_exercise_list}
              warn
            />
          </div>
          {(summary.pct_exercise_list ?? 0) === 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              No scraped class has exercise-list data yet — the column exists (FORT-shaped
              JSON array) but nothing populates it. You can hand-fill it (and muscle groups)
              per class via the edit action below.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search studio, class, category, instructor, muscle group…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <select
          value={studioFilter}
          onChange={(e) => setStudioFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="all">All studios</option>
          {studioOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={activityFilter}
          onChange={(e) => setActivityFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="all">All activities</option>
          {activityOptions.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          value={muscleGroupFilter}
          onChange={(e) => setMuscleGroupFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="all">All muscle groups</option>
          {muscleGroupOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={missingDataOnly}
            onChange={(e) => setMissingDataOnly(e.target.checked)}
          />
          Missing muscle groups & exercises
        </label>
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          Showing {filtered.length} of {classes.length}
        </span>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : loading ? (
        <div className="text-sm text-muted-foreground">Loading class catalog…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="w-8 px-2 py-2" />
                <SortHeader label="Studio" col="studio_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Class" col="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Category" col="category" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Duration" col="duration_min" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Muscle groups" col="muscle_groups" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th className="px-3 py-2 font-medium">Exercise list</th>
                <th className="px-3 py-2 font-medium">Other details</th>
                <SortHeader label="Seen" col="instance_count" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Next" col="next_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th className="px-3 py-2 font-medium">Book</th>
                <th className="px-3 py-2 font-medium text-right">Edit</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const key = groupKey(c)
                const isOpen = expanded.has(key)
                return (
                  <Fragment key={key}>
                    <tr className="border-t hover:bg-muted/30">
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => toggleExpanded(key)}
                          className="text-muted-foreground hover:text-foreground"
                          title={isOpen ? "Collapse" : "Expand"}
                        >
                          <ChevronRight className={cn("size-4 transition-transform", isOpen && "rotate-90")} />
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{c.studio_name}</div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {c.studio_activity_type && (
                            <Badge className={cn("font-normal capitalize", activityColor(c.studio_activity_type))}>
                              {c.studio_activity_type}
                            </Badge>
                          )}
                          {c.studio_city ?? ""}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-medium">{c.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {c.category ?? <span className="italic">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {c.duration_min ? `${c.duration_min} min` : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {c.muscle_groups.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {c.muscle_groups.map((m) => (
                              <Badge key={m} className="bg-slate-100 font-normal text-slate-700">{m}</Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs italic text-muted-foreground">not scraped</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {c.exercise_list.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {c.exercise_list.map((e) => (
                              <Badge key={e} className="bg-slate-100 font-normal text-slate-700">{e}</Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs italic text-muted-foreground">not scraped</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <OtherDetailsCell
                          details={c.other_details ?? ""}
                          disabled={readOnly}
                          onSave={(text) => handleSaveOtherDetails(c, text)}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.instance_count}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{c.next_date ?? "—"}</td>
                      <td className="px-3 py-2">
                        {c.booking_url ? (
                          <a
                            href={c.booking_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                          >
                            <ExternalLink className="size-3.5" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(c)}
                          disabled={readOnly}
                          title={readOnly ? "Read-only mode" : "Edit"}
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t bg-muted/20">
                        <td />
                        <td colSpan={11} className="space-y-2 px-3 py-3 text-xs">
                          <div>
                            <span className="font-medium text-muted-foreground">Instructors: </span>
                            {c.instructors.length > 0 ? c.instructors.join(", ") : "—"}
                          </div>
                          <div>
                            <span className="font-medium text-muted-foreground">Platform: </span>
                            {c.platform ?? "—"}
                          </div>
                          <div>
                            <span className="font-medium text-muted-foreground">Last scraped: </span>
                            {c.last_seen_at ?? "—"}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">
                    No classes match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {generatedAt && !loading && (
        <p className="text-xs text-muted-foreground">
          Generated {new Date(generatedAt).toLocaleString()}
        </p>
      )}

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setForm(null)
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit class</DialogTitle>
          </DialogHeader>

          {editing && form && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                {editing.studio_name} · applies to all {editing.instance_count} scraped
                occurrence{editing.instance_count === 1 ? "" : "s"} of this class.
              </p>

              <Field label="Class name">
                <Input value={form.name} onChange={(e) => setField("name", e.target.value)} />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <Input
                    value={form.category}
                    onChange={(e) => setField("category", e.target.value)}
                    placeholder="e.g. Strength, Yoga"
                  />
                </Field>
                <Field label="Duration (min)">
                  <Input
                    type="number"
                    min={1}
                    value={form.duration_min}
                    onChange={(e) => setField("duration_min", e.target.value)}
                    placeholder="45"
                  />
                </Field>
              </div>

              <Field label="Muscle groups" hint="Comma-separated, e.g. Quads, Glutes, Core">
                <Input
                  value={form.muscle_groups}
                  onChange={(e) => setField("muscle_groups", e.target.value)}
                  placeholder="Quads, Glutes, Core"
                />
              </Field>

              <Field label="Exercise list" hint="Comma-separated, e.g. Squat, Lunge, Deadlift">
                <Input
                  value={form.exercise_list}
                  onChange={(e) => setField("exercise_list", e.target.value)}
                  placeholder="Squat, Lunge, Deadlift"
                />
              </Field>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving || readOnly}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Inline notes editor — same click-to-expand pattern as Users & Access. */
function OtherDetailsCell({
  details,
  disabled,
  onSave,
}: {
  details: string
  disabled?: boolean
  onSave: (text: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(details)

  const start = () => {
    setDraft(details)
    setEditing(true)
  }
  const save = () => {
    onSave(draft.trim())
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="min-w-[260px] space-y-1.5">
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={5}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save()
            if (e.key === "Escape") setEditing(false)
          }}
          placeholder="Class description…"
          className="w-full resize-y rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-hidden focus:ring-2 focus:ring-primary"
        />
        <div className="flex items-center gap-1.5">
          <button
            onClick={save}
            className="inline-flex items-center gap-1 rounded bg-primary px-2 py-0.5 text-[11px] text-primary-foreground hover:bg-primary/90"
          >
            <Check className="size-3" /> Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded border px-2 py-0.5 text-[11px] hover:bg-accent"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if (!details) {
    return (
      <button
        onClick={start}
        disabled={disabled}
        title={disabled ? "Read-only mode" : "Add a description"}
        className="inline-flex items-center gap-1 rounded border border-dashed px-2 py-1 text-[11px] text-muted-foreground/60 hover:bg-accent hover:text-foreground disabled:opacity-40"
      >
        <StickyNote className="size-3" /> Description
      </button>
    )
  }

  return (
    <button
      onClick={start}
      disabled={disabled}
      title={disabled ? details : "Click to read or edit the full description"}
      className="-mx-1.5 flex max-w-[240px] items-start gap-1.5 rounded px-1.5 py-1 text-left hover:bg-accent/60 disabled:opacity-60"
    >
      <StickyNote className="mt-0.5 size-3 shrink-0 text-amber-500" />
      <span className="line-clamp-2 whitespace-pre-wrap wrap-break-word text-xs text-foreground/90">
        {details}
      </span>
    </button>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

function CoverageTile({
  label,
  pct,
  count,
  warn,
}: {
  label: string
  pct: number | null
  count: number
  warn?: boolean
}) {
  const value = pct ?? 0
  const isEmpty = warn && value === 0
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className={cn("text-2xl font-bold tabular-nums", isEmpty && "text-amber-600")}>
        {pct !== null ? `${pct}%` : "—"}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-[10px] text-muted-foreground">{count} classes</div>
    </div>
  )
}

function SortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string
  col: SortKey
  sortKey: SortKey
  sortDir: "asc" | "desc"
  onSort: (k: SortKey) => void
}) {
  const active = sortKey === col
  return (
    <th className="px-3 py-2 font-medium">
      <button
        onClick={() => onSort(col)}
        className={cn(
          "-mx-1 flex items-center gap-1 rounded px-1 py-0.5 hover:text-foreground",
          active && "text-foreground",
        )}
      >
        {label}
        {active ? (
          sortDir === "asc" ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />
        ) : (
          <ChevronsUpDown className="size-3.5 opacity-40" />
        )}
      </button>
    </th>
  )
}
